import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { create, InstanceProps } from 'react-modal-promise';
import { WMButtonVariant, WMDivider, WMIconPublish, WMSelect, WMSkeleton } from '@walkme/wm-ui';
import { EndRecurranceALL, EndRecurranceNONE, IEnvironment, NotificationTypeEnum } from '@communication-center/data-objects';
import pluralize from 'pluralize';
import { enqueueSnackbar } from '../../../state/actions/ui';
import { getNotificationPublishState, isNotificationScheduled, Notification } from '../../../models';
import StyledWMDialog from '../StyledWMDialog/StyledWMDialog';
import classes from './PublishNotificationConfirm.module.scss';
import logger from '../../../services/logger.service';
import DependenciesPublishNote from './DependenciesPublishNote/DependenciesPublishNote';
import DeliveryTimeText from './DeliveryTimeText/DeliveryTimeText';
import { getUnPublishedDependencies, publishDependencies, usePublishNotifications } from './helpers';
import { useAudiencesLists } from '../../Audiences/hooks/hooks';
import { usePlayableItems } from '../../AppPreloading/hooks/walkmeData/usePlayableItems';
import IStore from '../../../infrastructure/store.interface';
import { reoccurrencesDescription } from '../../EditNotification/EditNotificationContent/SendStepDrawer/SelectReoccurrence/reoccurrencesDescription';
import { ReactComponent as ReoccurenceIcon } from '../../../../assets/icons/reoccurence.svg';
import { ReactComponent as NotificationTypeIcon } from '../../../../assets/icons/notification-type.svg';
import { ReactComponent as TargetAudienceIcon } from '../../../../assets/icons/target-audience-icon.svg';
import { ReactComponent as IndividualRecipientsIcon } from '../../../../assets/icons/add-user.svg';
import { ReactComponent as UserClicksIcon } from '../../../../assets/icons/user-clicks.svg';
import { AudienceOption, getAudiencesOptions } from '../../../helpers/audiences';
import cc from 'classcat';
import {
    GetAllRecipientsDataQuery,
    RecipientsFilter,
    useGetAllRecipientsDataQuery,
    useGetNotificationRecipientsCountQuery
} from '../../../../generated/apollo-hooks';
import { extractButtons, extractXButtons } from '../../../helpers/visualDesign/extractVisualDesignDataProps';
import { useWalkmeSdkService } from '../../../infrastructure/WalkmeSdkProvider/useWalkmeSdkService';
import { useTranslation } from 'react-i18next';
import { WMLoader } from './WMLoader';

const keyPrefix = 'components.dialogs.publishNotificationConfirm.publishNotificationConfirm';

const AllRecipients = ({ data }: { data?: GetAllRecipientsDataQuery }) => {
    const { t } = useTranslation('', { keyPrefix });
    const title = t('allRecipientsText');
    const size = data?.getAllRecipientsData?.activeEndUsersCount;
    return (
        <div
            className={cc([classes.audienceDataListItem, classes.audienceDataListItemAllRecipients, { [classes.link]: size !== 0 }])}
            data-testid={`publish-confirm-selected-audience-all_recipients-chip`}
        >
            {title}
        </div>
    );
};



interface IPublishNotificationsConfirmProps extends InstanceProps<{ id: string; value: string } | undefined> {
    environments: IEnvironment[];
    currentEnvironmentId: string;
    systemId: string;
    notifications: Notification[];
    isResuming?: boolean;
    isPausing?: boolean;
}

export const PublishNotificationsConfirm = ({
    isOpen,
    onResolve,
    environments,
    currentEnvironmentId,
    systemId,
    notifications,
    isResuming,
    isPausing = false
}: IPublishNotificationsConfirmProps) => {
    const { t } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    const store = useStore<IStore>();
    const publishNotifications = usePublishNotifications();
    const { audiencesList } = useAudiencesLists();
    const { walkmeSdkService } = useWalkmeSdkService();

    const notification = notifications?.[0];

    const audiences = (getAudiencesOptions(notification?.audiencesIds || [], audiencesList || []) as AudienceOption[]) || [];
    const endUsers = notification?.endUsersIds || [];

    const targetAllRecipeints = notification?.isAllAudienceSelected || (!audiences.length && !endUsers.length);

    const environmentOptions = environments.map(e => {
        return { label: e.label, value: e.id, id: e.id };
    });

    const [selectedEnv, setSelectedEnv] = useState(environmentOptions.find(e => e.id === currentEnvironmentId)!);
    const [publishLoader, setPublishLoader] = useState(false);

    const decline = useCallback(() => {
        onResolve(undefined);
    }, [onResolve]);

    const { playableItems } = usePlayableItems({ environmentId: selectedEnv?.id, onError: () => decline() });

    const loading = !playableItems;

    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ __typename?: "PlayableItems" | undefined; ... Remove this comment to see the full error message
    const unPublishedDependencies = useMemo(() => getUnPublishedDependencies({ notifications, playableItems }), [notifications, playableItems]);

    const confirm = async () => {
        setPublishLoader(true);
        const impersonator = store.getState().common?.user.impersonator;

        try {
            await publishDependencies(walkmeSdkService, unPublishedDependencies, selectedEnv.id, !!impersonator);
            await publishNotifications(notifications, systemId, selectedEnv.id, !!isResuming, isPausing);

            onResolve({ id: selectedEnv.id, value: selectedEnv.value });
        } catch (error) {
            const errorText = error && typeof error === 'object' && 'message' in error ? ': ' + error.message : '';
            const message = `${t('snackbar', { count: notifications.length })}${errorText}`;
            const logMessage = `Could not save ${pluralize('notification', notifications.length)}${errorText}`;

            dispatch(enqueueSnackbar({ message, options: { variant: 'error' } }));

            logger.error(logMessage, error, {
                isResuming,
                selectedEnv: selectedEnv.id,
                unPublishedDependencies,
                notifications
            });

            onResolve(undefined);
        }
    };

    const buttons = [
        {
            children: t('cancel'),
            variant: WMButtonVariant.Primary,
            className: classes.cancelButton,
            onClick: () => decline(),
            'data-testid': 'publish-notification-dialog-decline'
        },
        {
            children: t('send'),
            variant: WMButtonVariant.Primary,
            disabled: !playableItems,
            onClick: () => confirm(),
            'data-testid': 'publish-notification-dialog-confirm'
        }
    ];
    const notificationType = notification.type === NotificationTypeEnum.PopUp ? t('popup') : t('push');
    const isScheduled = isNotificationScheduled(notification, currentEnvironmentId);

    const count = notifications?.length;

    const title = publishLoader ? `` : `${isResuming ? t('title.resume', { count }) : t('title.review', { count })}`;
    const publishLoaderTitle = isScheduled ? t('publishLoaderTitle.scheduled', { count }) : t('publishLoaderTitle.activated', { count });

    const environmentId = selectedEnv.id;
    const publishState = useMemo(() => (notification && environmentId ? getNotificationPublishState(notification, environmentId) : null), [
        notification,
        environmentId
    ]);
    const reoccurrenceData = publishState?.scheduling?.recurrence;
    const rawRepeatText = reoccurrencesDescription(reoccurrenceData);
    const repeatText = rawRepeatText && rawRepeatText?.length > 0 ? rawRepeatText[0].toUpperCase() + rawRepeatText.slice(1) : rawRepeatText;
    const visualDesignData = notification?.visualDesignData;

    const buttonsOptions = useMemo(() => {
        if (!visualDesignData) return [];

        const buttons = extractButtons(visualDesignData).map(buttonAction => ({ value: buttonAction.id, label: buttonAction.label }));
        const XButton = extractXButtons(visualDesignData).map(buttonAction => ({ value: buttonAction.id, label: 'X' }))[0];
        return [...buttons, ...(XButton ? [XButton] : [])];
    }, [visualDesignData]);

    const stopPlayingBtns = publishState?.scheduling?.recurrence?.endRecurrenceButtons;

    const stopPlayingBtnText = useMemo(() => {
        if (!stopPlayingBtns || stopPlayingBtns === EndRecurranceNONE) return null;

        if (stopPlayingBtns === EndRecurranceALL) return t('allButtons');

        return stopPlayingBtns
            .map((buttonId: $TSFixMe) =>
                t('stopPlayingBtn', {
                    name: buttonsOptions.find(button => button.value === buttonId)?.label
                })
            )
            .join(', ');
    }, [stopPlayingBtns, t, buttonsOptions]);

    const { data: recipientsCountData, loading: loadingRecipientsCount } = useGetNotificationRecipientsCountQuery({
        variables: {
            systemId,
            audienceIds: notification.audiencesIds || [],
            endUsersIds: notification.endUsersIds || [],
            recipientsFilter: RecipientsFilter.Active
        },
        skip: !audiences.length && !endUsers.length
    });

    const { data: allRecipientsData, loading: getAllRecipientsDataQueryLoading } = useGetAllRecipientsDataQuery({
        variables: { systemId: systemId! },
        skip: !!audiences.length
    });

    const totalRecipients = targetAllRecipeints
        ? allRecipientsData?.getAllRecipientsData?.activeEndUsersCount
        : recipientsCountData?.getNotificationRecipientsCount;

    const isLoadingTotalRecipients = targetAllRecipeints ? getAllRecipientsDataQueryLoading : loadingRecipientsCount;

    return (
        <StyledWMDialog
            title={title}
            open={!!isOpen}
            onClose={decline}
            buttons={publishLoader ? undefined : buttons}
            hideCloseButton={publishLoader}
            data-testid={'publish-notification-dialog'}
            classes={{
                content: classes.dialogContent,
                header: publishLoader ? classes.dialogHeader : '',
                headerDivider: publishLoader ? classes.dialogHeaderDivider : ''
            }}
            className={cc({ [classes.publishLoader]: publishLoader })}
        >
            {publishLoader ? (
                <div className={classes.publishLoaderContent} data-testid={'publish-notification-loader'}>
                    <WMLoader className={classes.wmloader} />
                    <div className={classes.publishLoaderTitle}>{publishLoaderTitle}</div>
                    <div className={classes.publishLoaderSubtitle}>{t('publishLoaderSubtitle')}</div>
                </div>
            ) : (
                <div className={classes.publishNotificationDialogContent}>
                    <DependenciesPublishNote loading={loading} unPublishedDependencies={unPublishedDependencies} />

                    <WMSelect
                        label={t('sendNotificationTo', { count: notifications.length })}
                        options={environmentOptions}
                        value={selectedEnv}
                        onChange={item => {
                            return setSelectedEnv(item as typeof environmentOptions[number]);
                        }}
                    />
                    {notifications.length == 1 && (
                        <div>
                            <div className={classes.activeSum}>
                                <WMSkeleton condition={isLoadingTotalRecipients || typeof totalRecipients === undefined}>
                                    <span>{t('activeSum', { count: totalRecipients ?? 0 })}</span>
                                </WMSkeleton>
                            </div>

                            {(audiences.length || targetAllRecipeints) && (
                                <div className={cc([classes.section, { [classes.largeMarginBottom]: endUsers.length > 0 }])}>
                                    <div className={cc([classes.iconWrapper, classes.iconBgBlue])}>
                                        <TargetAudienceIcon color="#385FEB" />
                                    </div>
                                    <div>
                                        <div className={classes.sectionTitle}>{t('audiencesTitle')}</div>
                                        <div className={classes.sectionContent}>
                                            {!!audiences.length && <div>{t('audiencesContent', { count: audiences.length })}</div>}
                                            {!!targetAllRecipeints && (
                                                <WMSkeleton condition={getAllRecipientsDataQueryLoading}>
                                                    <AllRecipients data={allRecipientsData} />
                                                </WMSkeleton>
                                            )}
                                        </div>
                                    </div>
                                    {!!audiences.length && (
                                        <div className={classes.sectionBottomContent}>
                                            <div className={classes.audienceDataList}>
                                                {audiences.map(({ label, size }) => (
                                                    <div
                                                        key={label}
                                                        className={cc([classes.audienceDataListItem])}
                                                        data-testid={`publish-confirm-selected-audience-${label}-chip`}
                                                    >
                                                        {`${label} (${size})`}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!!endUsers?.length && (
                                <div className={cc([classes.section])}>
                                    <div className={cc([classes.iconWrapper, classes.iconBgBlue])}>
                                        <IndividualRecipientsIcon color="#385FEB" />
                                    </div>
                                    <div>
                                        <div className={classes.sectionTitle}>{t('endUsersTitle')}</div>
                                        <div className={classes.sectionContent}>
                                            <div>{t('endUsersContent', { count: endUsers.length })}</div>
                                        </div>
                                    </div>
                                    <div className={classes.sectionBottomContent}>
                                        <div className={classes.audienceDataList}>
                                            {endUsers.map(endUserId => (
                                                <div
                                                    key={endUserId}
                                                    className={cc([classes.audienceDataListItem])}
                                                    data-testid={`publish-confirm-selected-recipient-${endUserId}-chip`}
                                                >
                                                    {endUserId}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <WMDivider className={classes.divider} />

                            <div className={classes.section}>
                                <div className={cc([classes.iconWrapper, classes.iconBgGreen])}>
                                    <WMIconPublish color="#1F5850" />
                                </div>
                                <div>
                                    <div className={classes.sectionTitle}>{t('deliveryTime')}</div>
                                    <DeliveryTimeText notification={notifications[0]} environmentId={currentEnvironmentId} />
                                </div>
                            </div>
                            {!!reoccurrenceData && (
                                <div className={classes.section}>
                                    <div className={cc([classes.iconWrapper, classes.iconBgGreen])}>
                                        <ReoccurenceIcon className={classes.reoccurrenceIcon} />
                                    </div>
                                    <div>
                                        <div className={classes.sectionTitle}>{t('recurrence')}</div>
                                        <div className={classes.sectionContent}>{repeatText}</div>
                                    </div>
                                </div>
                            )}
                            {!!stopPlayingBtns && !stopPlayingBtns.includes(EndRecurranceNONE) && (
                                <div className={classes.section}>
                                    <div className={cc([classes.iconWrapper, classes.iconBgGreen])}>
                                        <UserClicksIcon className={classes.reoccurrenceIcon} />
                                    </div>
                                    <div>
                                        <div className={classes.sectionTitle}>{t('stopPlaying')}</div>
                                        <div className={classes.sectionContent}>{stopPlayingBtnText}</div>
                                    </div>
                                </div>
                            )}
                            <div className={classes.section}>
                                <div className={cc([classes.iconWrapper, classes.iconBgPink])}>
                                    <NotificationTypeIcon />
                                </div>
                                <div>
                                    <div className={classes.sectionTitle}>{t('notificationType')}</div>
                                    <div className={classes.sectionContent}>{notificationType}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </StyledWMDialog>
    );
};

export const publishNotificationsConfirm = create(PublishNotificationsConfirm);
